import { Activity, ArrowRight, Ruler, ShieldAlert, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import type { PatientCardData } from '@/components/medic/patientCardData';
import { summarizeRecords } from '@/components/medic/patientCardSummary';
import type { PatientCardTab } from '@/components/medic/PatientCardOverview';

// ============================================================================
// PatientCriticalBar — КОМПАКТНАЯ полоса «Важно для вызова» вверху карточки
// пациента: аллергии, действующие диагнозы, последние измерения, последние
// ЭКГ. Только добавление к «Основному» (сам блок не ломаем).
// Адрес/пол/год остаются в PatientProfileBlock — здесь НЕ дублируются.
// ============================================================================

interface Props {
    data: PatientCardData;
    onGoTab: (tab: PatientCardTab) => void;
    onOpenStudy: (studyId: string) => void;
}

function stateNote(state: string): string {
    if (state === 'ready') return '';
    if (state === 'denied') return 'Нет доступа';
    return 'Недоступно';
}

function chips(items: string[], empty: string, limit = 4): string {
    if (items.length === 0) return empty;
    const shown = items.slice(0, limit);
    const rest = items.length - shown.length;
    return rest > 0 ? `${shown.join(' · ')} · ещё ${rest}` : shown.join(' · ');
}

function Cell({
    icon,
    title,
    tone,
    onMore,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    tone?: 'danger' | 'warning';
    onMore?: () => void;
    children: React.ReactNode;
}) {
    const border = tone === 'danger'
        ? 'border-destructive/40 bg-destructive/5'
        : tone === 'warning'
            ? 'border-amber-500/40 bg-amber-500/5'
            : 'border-border bg-card';
    const text = tone === 'danger'
        ? 'text-destructive'
        : tone === 'warning'
            ? 'text-amber-700 dark:text-amber-400'
            : 'text-primary';
    return (
        <div className={`flex min-w-0 flex-col rounded-lg border p-2 ${border}`}>
            <div className="flex items-center gap-1.5 text-[11px] font-medium">
                <span className={text}>{icon}</span>
                <span className="truncate">{title}</span>
                {onMore && (
                    <button
                        type="button"
                        onClick={onMore}
                        className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-normal text-muted-foreground hover:text-foreground"
                    >
                        все <ArrowRight className="h-2.5 w-2.5" />
                    </button>
                )}
            </div>
            <div className="mt-1 min-w-0 text-xs leading-snug">{children}</div>
        </div>
    );
}

export function PatientCriticalBar({ data, onGoTab, onOpenStudy }: Props) {
    const records = data.records;
    const summary = summarizeRecords(records);

    const ecgReady = data.ecg.state === 'ready';
    const anyReady = records.allergies.state === 'ready'
        || records.diagnoses.state === 'ready'
        || records.measurements.state === 'ready'
        || ecgReady;
    if (!anyReady) return null;

    const allergies = summary.activeAllergies.map((item) => item.title);
    const diagnoses = summary.activeDiagnoses.map((item) => item.title);
    const latestDiagnosis = records.diagnoses.state === 'ready' ? records.diagnoses.items[0] : null;
    const measurements = records.measurements.state === 'ready'
        ? records.measurements.items.slice(0, 3)
        : [];
    const studies = ecgReady ? data.ecg.studies.slice(0, 2) : [];

    return (
        <section className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                <ShieldAlert className="h-3.5 w-3.5 text-destructive" />
                Важно для вызова
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <Cell
                    icon={<ShieldAlert className="h-3.5 w-3.5" />}
                    title="Аллергии"
                    tone={allergies.length > 0 ? 'danger' : undefined}
                    onMore={() => onGoTab('allergies')}
                >
                    {records.allergies.state === 'ready' ? (
                        <span className={allergies.length > 0 ? 'font-medium text-destructive' : 'text-muted-foreground'}>
                            {chips(allergies, 'Аллергий не зафиксировано')}
                        </span>
                    ) : (
                        <span className="text-muted-foreground">{stateNote(records.allergies.state)}</span>
                    )}
                </Cell>
                <Cell
                    icon={<Stethoscope className="h-3.5 w-3.5" />}
                    title="Диагнозы"
                    tone={diagnoses.length > 0 ? 'warning' : undefined}
                    onMore={() => onGoTab('diagnoses')}
                >
                    {records.diagnoses.state === 'ready' ? (
                        <span className={diagnoses.length > 0 ? 'font-medium' : 'text-muted-foreground'}>
                            {chips(
                                diagnoses,
                                latestDiagnosis ? `Ранее: ${latestDiagnosis.title}` : 'Диагнозов нет',
                                3,
                            )}
                        </span>
                    ) : (
                        <span className="text-muted-foreground">{stateNote(records.diagnoses.state)}</span>
                    )}
                </Cell>
                <Cell
                    icon={<Ruler className="h-3.5 w-3.5" />}
                    title="Последние измерения"
                    onMore={() => onGoTab('measurements')}
                >
                    {records.measurements.state !== 'ready' ? (
                        <span className="text-muted-foreground">{stateNote(records.measurements.state)}</span>
                    ) : measurements.length === 0 ? (
                        <span className="text-muted-foreground">Измерений нет</span>
                    ) : (
                        <ul className="space-y-0.5">
                            {measurements.map((item) => (
                                <li key={item.id} className="flex items-baseline justify-between gap-2">
                                    <span className="truncate font-medium">{item.title}</span>
                                    <span className="shrink-0 text-[10px] text-muted-foreground">{item.date}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Cell>
                <Cell icon={<Activity className="h-3.5 w-3.5" />} title="Последние ЭКГ">
                    {!ecgReady ? (
                        <span className="text-muted-foreground">{stateNote(data.ecg.state)}</span>
                    ) : studies.length === 0 ? (
                        <span className="text-muted-foreground">Исследований нет</span>
                    ) : (
                        <div className="space-y-1">
                            {studies.map((study) => (
                                <Button
                                    key={study.id}
                                    size="sm"
                                    variant="outline"
                                    className="h-6 w-full justify-between px-1.5 text-[11px] font-normal"
                                    onClick={() => onOpenStudy(study.id)}
                                >
                                    <span className="truncate">{study.statusLabel || 'ЭКГ'}</span>
                                    <span className="shrink-0 text-[10px] text-muted-foreground">
                                        {formatValue(study.createdAt, 'date:dd.MM.yyyy HH:mm', 'timestamp')}
                                    </span>
                                </Button>
                            ))}
                        </div>
                    )}
                </Cell>
            </div>
        </section>
    );
}
